import { useEffect, useRef } from 'react'
import { useStoreActions, useStoreState } from '../store/useStore'
import type { FormStepResult, FMPCompletionMetadata } from '../types'
import { Questionnaire } from './components/Questionnaire'
import { useIncense } from '../hooks/useIncense'
import { useStepLifecycle } from './hooks/useStepLifecycle'
import useFormStarted from '@strata-monorepo/tracking/src/hooks/useFormStarted'
import useFormContinued from '@strata-monorepo/tracking/src/hooks/useFormContinued'
import useFormViewed from '@strata-monorepo/tracking/src/hooks/useFormViewed'
import useFormSubmitted from '@strata-monorepo/tracking/src/hooks/useFormSubmitted'
import useCoreIdentify from '@strata-monorepo/tracking/src/hooks/useCoreIdentify'
import { trackFunnelAction } from '../utils/trackFunnelAction'
import {
  buildFieldArrayFromFinalFormResults,
  buildFieldArrayFromFormResults,
} from '../utils/buildFieldArrayFromStep'
import { DomainEnum, QuestionFlowEnum } from '@strata-monorepo/shared-types'
import type { FuseConfig } from '@strata-monorepo/shared-types/src/fuse'
import { resolveTrackingValues, type TrackingWithVariants } from '../utils/resolveTrackingValues'
import { useMonarch, useMonarchRule } from '@redventures/cohesion-utils-react'
import { validateEmail } from '../utils/validateEmail'
import normalizeAddressResult from '../utils/normalizeAddressResult'
import { QuestionnaireActionsContext } from './QuestionnaireActionsContext'
import { FunnelActionsCookies } from '@strata-monorepo/utils/constants/funnelActionsCookies'
import { getRevisionId } from '@strata-monorepo/tracking/src/hooks/getRevisionId'
import usePageViewed from '@strata-monorepo/tracking/src/hooks/usePageViewed'
import { logRocketLog } from '@strata-monorepo/utils/logRocket'
import { useQuestionnaireSelection } from '../hooks/useQuestionnaireSelection'
import { userAlreadyStartedGuard } from '../hooks/useQuestionnaireSelection/guards'
import { createUrlQuestionnaireStrategy } from '../hooks/useQuestionnaireSelection/strategies/UrlQuestionnaireStrategy'
import { createDefaultFormResultsStrategy } from '../hooks/useQuestionnaireSelection/strategies/DefaultFormResultsStrategy'
import { createMonarchStrategy } from '../hooks/useQuestionnaireSelection/strategies/MonarchStrategy'
import { createFallbackStrategy } from '../hooks/useQuestionnaireSelection/strategies/FallbackStrategy'
import { setQuestionnaireInUrl } from '../utils/getQuestionnaireFromUrl'
import { persistLpAnswers } from '../utils/lpAnswers'

interface QuestionSetRuleValue {
  data: {
    questionnaire: string
  }
}

interface QuestionnaireWrapperProps {
  /**
   * Optional default form results to pre-populate the form
   */
  defaultFormResults?: FormStepResult

  /**
   * Optional callback function that will be called when the form is completed
   * @param results The final form results
   * @param metadata Completion context including skip intent and flow type
   */
  onComplete?: (results: FormStepResult, metadata?: FMPCompletionMetadata) => void
  onErrorCta?: (results: FormStepResult) => void

  /**
   * Optional Fuse phone number configurations passed from the host
   */
  fuseConfigs?: FuseConfig[]
}

export function QuestionnaireWrapper({
  defaultFormResults = {},
  fuseConfigs = [],
  onComplete,
  onErrorCta,
}: QuestionnaireWrapperProps) {
  // Get actions from the store
  const {
    setFormResults,
    handleFormCompletion,
    handleErrorCta,
    handleNextStep,
    handlePrevStep,
    setFilteredQuestions,
    setMonarchQuestionnaireName,
  } = useStoreActions((actions) => actions.form)
  const { initApp } = useStoreActions((actions) => actions.app)
  const { fetchConsumptionPrediction } = useStoreActions((actions) => actions.consumptionPrediction)
  const { fetchEnrichCustomerData } = useStoreActions((actions) => actions.enrichCustomer)
  const { setFuseConfigs } = useStoreActions((actions) => actions.phoneConfig)

  // Hooks
  const incense = useIncense()
  const { isLoading: monarchIsLoading, err, rules } = useMonarch()
  const trackPageViewed = usePageViewed()
  const { handleStepLoaded } = useStepLifecycle()
  const trackCoreIdentify = useCoreIdentify()
  const trackFormStarted = useFormStarted()
  const trackFormViewed = useFormViewed()

  // Get state
  const { metricsClient, isMetricsProviderLoaded } = useStoreState((state) => state.metrics)
  const { siteDomain } = useStoreState((state) => state.siteConfig)
  const currentStep = useStoreState((state) => state.form.currentStep)
  const currentStepIndex = useStoreState((state) => state.form.currentStepIndex)
  const formId = useStoreState((state) => state.form.formId)
  const formVersion = useStoreState((state) => state.form.formVersion)
  const formResults = useStoreState((state) => state.form.formResults)
  const lastCompletedStepName = useStoreState((state) => state.form.lastCompletedStepName)
  const isDeepDive = useStoreState((state) => state.form.isDeepDiveFlow)
  const formType = useStoreState((state) => state.form.formType)
  const filteredQuestions = useStoreState((state) => state.form.filteredQuestions)
  const filterQuestions = useStoreState((state) => state.form.filterQuestions)
  const monarchQuestionnaireName = useStoreState((state) => state.form.monarchQuestionnaireName)
  const enrichCustomerResponse = useStoreState(
    (state) => state.enrichCustomer.enrichCustomerResponse,
  )
  const consumptionPredictionResponse = useStoreState(
    (state) => state.consumptionPrediction.consumptionPredictionResponse,
  )

  // Track if FormStarted event has been sent to prevent duplicates
  const hasTrackedFormStarted = useRef(false)
  const trackFormContinued = useFormContinued()

  const trackFormSubmitted = useFormSubmitted()

  // Track if FindMyPlanInit event has fired to prevent duplicates
  const hasFindMyPlanInitDOMEventFired = useRef<boolean>(false)

  // Tracks whether the current completion was triggered by Skip to Results
  const wasSkippedToResultsRef = useRef<boolean>(false)

  const { value: questionSetData } = useMonarchRule<QuestionSetRuleValue>('fmpQuestionSets')
  const questionSet = questionSetData?.data.questionnaire

  // Initialize app
  useEffect(() => {
    initApp()
  }, [initApp])

  useEffect(() => {
    setFuseConfigs(fuseConfigs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuseConfigs])

  // Questionnaire selection using Strategy Pattern
  useQuestionnaireSelection({
    context: {
      defaultFormResults,
      monarchQuestionnaireName,
      currentStepIndex,
      monarchIsLoading,
      monarchError: err as Error | null,
      questionSet: questionSet ?? undefined,
    },
    strategies: [
      createUrlQuestionnaireStrategy(),
      createDefaultFormResultsStrategy(),
      createMonarchStrategy(),
      createFallbackStrategy(),
    ],
    guards: [userAlreadyStartedGuard],
    onSelectionChange: setMonarchQuestionnaireName,
  })

  // Keep the resolved questionnaire on the current FMP history entry so browser Back
  // restores the same flow without adding another entry.
  useEffect(() => {
    if (monarchQuestionnaireName) {
      setQuestionnaireInUrl(monarchQuestionnaireName)
    }
  }, [monarchQuestionnaireName])

  // This is triggered when we load a new step
  // Also to fire the FormViewed event
  useEffect(() => {
    if (currentStep === null) return

    const stepTracking = currentStep?.componentProps?.tracking as TrackingWithVariants | undefined

    trackFormViewed({
      formContext: {
        formName: formVersion,
        formId,
        formType,
      },
      stepContext: {
        stepName: stepTracking?.stepName || '',
        stepNumber: currentStepIndex || 0,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  useEffect(() => {
    /**
     * IMPORTANT: Do not remove this code
     * This custom DOM event notifies the parent window that FindMyPlan has been initialized
     * Example: CTXP listens for this event to remove the loader
     */
    if (typeof window !== 'undefined' && !hasFindMyPlanInitDOMEventFired.current) {
      hasFindMyPlanInitDOMEventFired.current = true
      window.dispatchEvent(new CustomEvent('findMyPlan:initialized'))
      const revisionId = getRevisionId('_fmp')
      trackPageViewed({ revisionId, pageName: 'find-my-plan' })
    }
  }, [trackPageViewed])

  // Update filtered questions when questionnaire changes
  // This ensures that when monarchQuestionnaireName changes from null to a valid value,
  // the filtered questions are populated
  useEffect(() => {
    // Only update if we have a questionnaire loaded
    if (!monarchQuestionnaireName) return

    // Apply banned questions filter if available
    if (!monarchIsLoading && !err && rules['fmpBannedQuestions']) {
      const bannedQuestions: string[] = (rules.fmpBannedQuestions.value || []) as string[]
      setFilteredQuestions(filterQuestions(bannedQuestions))
    } else if ((err || !rules['fmpBannedQuestions']) && !monarchIsLoading) {
      // If there's an error or no fmpBannedQuestions rule exists, use all questions (no filtering)
      setFilteredQuestions(filterQuestions([]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monarchQuestionnaireName, monarchIsLoading, err, rules])

  useEffect(() => {
    if (isMetricsProviderLoaded && metricsClient) {
      metricsClient.increment('fmp.questionnaire.loaded')
    }
  }, [isMetricsProviderLoaded, metricsClient])

  // Intentional empty dep array — this must fire exactly once on mount.
  // trackFormStarted returns a new function reference on every render so adding
  // it to deps would re-trigger the effect on every render.
  // formId and formVersion are stable at mount time (formId is set at store
  // creation before first render; formVersion is a constant string).
  useEffect(() => {
    if (hasTrackedFormStarted.current) return

    // Check if FormStarted event was already fired externally (e.g., from CTXP)
    // Note: We do NOT clear this flag from sessionStorage to persist across
    // component remounts within the same session
    const externalFormStarted =
      typeof window !== 'undefined' && sessionStorage.getItem('fmpFormStartedExternal') === 'true'

    if (externalFormStarted) {
      hasTrackedFormStarted.current = true
      sessionStorage.removeItem('fmpFormStartedExternal')
      return
    }

    trackFormStarted({
      formContext: {
        formName: formVersion,
        formType: '',
        formId,
      },
      stepContext: {
        stepId: '',
        stepName: 'Quiz Start',
        stepNumber: 0,
      },
    })
    hasTrackedFormStarted.current = true

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initialize form with default values if provided
  useEffect(() => {
    if (Object.keys(defaultFormResults).length > 0) {
      setFormResults(defaultFormResults)
    }
  }, [defaultFormResults, setFormResults])

  // Handle form completion
  const handleFinish = (results: FormStepResult) => {
    const metadata: FMPCompletionMetadata = {
      wasSkippedToResults: wasSkippedToResultsRef.current,
      isLongFlow: isDeepDive,
    }

    // Reset skip flag before any async work so it doesn't bleed into future completions
    wasSkippedToResultsRef.current = false

    handleFormCompletion(results)

    try {
      // `currentStep` reflects the step that was on screen when the user
      // completed the quiz — it's set when a step mounts, so it's accurate
      // here. `lastCompletedStepName` is stale: Odyssey calls onNext (which
      // updates it) synchronously right before onFinish, before React can
      // re-render this closure, so it still holds the PRIOR step's name
      // (or '' on a single-question flow, where no prior step exists).
      const lastQuestion = currentStep

      const stepTracking = lastQuestion?.componentProps?.tracking as
        TrackingWithVariants | undefined
      const resolvedTracking = resolveTrackingValues(stepTracking, isDeepDive, 0)
      const fieldArray = buildFieldArrayFromFinalFormResults(results)

      // Guard against undefined lastQuestion
      if (!lastQuestion) {
        logRocketLog('[FormSubmitted] Skipped: lastQuestion not found', {
          lastCompletedStepName,
          formId,
        })
      } else {
        logRocketLog('[FormSubmitted] Tracking form submission', {
          formId,
          formVersion,
          formType,
          lastCompletedStepName: lastQuestion.name,
          stepNumber: currentStepIndex ?? 0,
          fieldCount: fieldArray.length,
        })

        trackFormSubmitted({
          formContext: {
            formName: formVersion,
            formType,
            formId,
          },
          stepContext: {
            stepId: resolvedTracking?.stepId || '0',
            stepName: resolvedTracking?.stepName || lastQuestion.name,
            stepNumber: currentStepIndex ?? 0,
          },
          field: fieldArray,
        })
      }
    } catch (error) {
      console.error('Error tracking FormSubmitted event:', error, {
        lastCompletedStepName,
        formId,
        isDeepDive,
      })
    }

    persistLpAnswers(results)

    if (onComplete) {
      onComplete(results, metadata)
    }

    if (metricsClient) {
      metricsClient.increment('fmp.questionnaire.completed')
    }
  }

  // This event is to handle specific methods or operations when certain steps are completed
  const handleStepsCallbacks = (stepName: string, results: FormStepResult) => {
    // This is ONLY for the email question, where we have to send code.identify.v1 event
    if (stepName === 'textQuestion' && results?.email && validateEmail(results?.email)) {
      const { street_line, state, zipcode, city } = normalizeAddressResult(results?.address)
      const email = results?.email

      trackCoreIdentify({
        address: {
          street: street_line,
          state,
          postalCode: zipcode,
          city,
        },
        email,
      })
    }

    // This is ONLY for the interstitial radio group, where we have to save a cookie to track the user's choice
    if (stepName === 'interstitialRadioGroup') {
      const flowType =
        results.interstitial === QuestionFlowEnum.DEEP_DIVE
          ? FunnelActionsCookies.FMP_QUIZ_LONG
          : FunnelActionsCookies.FMP_QUIZ_SHORT

      trackFunnelAction(flowType, siteDomain || DomainEnum.CTXP)
    }
  }

  /**
   * Immediately completes the quiz with the current accumulated form results.
   * Called when the user activates the Skip to Results header action.
   *
   * If enrichment data is missing (user skipped before AddressAndMover completed),
   * attempts to fetch it from formResults.address before fetching consumption prediction.
   */
  const handleSkipToResults = async () => {
    wasSkippedToResultsRef.current = true

    const predictionAlreadyFetched =
      consumptionPredictionResponse && Object.keys(consumptionPredictionResponse).length > 0

    if (!predictionAlreadyFetched) {
      const cohesionData = (window as Window & typeof globalThis)._Cohesion || {}

      let resolvedEnrichResponse = enrichCustomerResponse

      if (!resolvedEnrichResponse) {
        const addressFromForm = formResults.address as
          | {
              zipcode?: string
              city?: string
              state?: string
              street_line?: string
              secondary?: string
            }
          | undefined

        if (addressFromForm?.zipcode) {
          try {
            resolvedEnrichResponse = await fetchEnrichCustomerData({
              CohesionGlobal: cohesionData,
              address: {
                zipcode: addressFromForm.zipcode,
                city: addressFromForm.city || '',
                state: addressFromForm.state || '',
                street_line: addressFromForm.street_line || '',
                secondary: addressFromForm.secondary,
              },
            })
          } catch {
            // Allow skip completion even if enrich-customer fails
          }
        }
      }

      if (resolvedEnrichResponse) {
        try {
          await fetchConsumptionPrediction({
            cohesionData,
            enrichedData: resolvedEnrichResponse,
            allEntries: [],
          })
        } catch {
          // Allow skip completion even if prediction fails — same tolerance as the normal flow
        }
      }
    }

    handleFinish(formResults)
  }

  // Handle next step
  const handleNext = (stepName: string, results: FormStepResult) => {
    // Find the step that was just completed by stepName (not by currentStepIndex)
    const completedStep = filteredQuestions.find((step) => step.name === stepName)
    if (!completedStep) {
      handleNextStep({ stepName, results })
      return
    }

    handleStepsCallbacks(stepName, results)

    const stepTracking = completedStep?.componentProps?.tracking as TrackingWithVariants | undefined

    // Build field array from the submitted results
    const fieldArray = buildFieldArrayFromFormResults(completedStep, results)

    // Get the completed step index for fallback values
    const completedStepIndex = filteredQuestions.findIndex((step) => step.name === stepName)

    // Resolve tracking values for this specific step
    const resolvedTracking = resolveTrackingValues(
      stepTracking,
      isDeepDive,
      completedStepIndex >= 0 ? completedStepIndex : undefined,
    )

    // Track form continuation with the just-submitted data
    logRocketLog('[FormContinued] Tracking step completion', {
      formId,
      formVersion,
      stepName,
      completedStepIndex,
      fieldCount: fieldArray.length,
    })

    trackFormContinued({
      field: fieldArray,
      formContext: {
        formName: formVersion,
        formId,
        formType,
      },
      stepContext: {
        stepId: resolvedTracking?.stepId || `step-${completedStepIndex}`,
        stepName: resolvedTracking?.stepName || stepName,
        stepNumber: currentStepIndex ?? completedStepIndex,
      },
    })

    if (metricsClient) {
      metricsClient.increment('fmp.questionnaire.next', 1, {
        stepName,
      })
    }

    // Use the store action to handle next step
    handleNextStep({ stepName, results })
  }

  // Handle back step
  const handleBack = (stepName: string, results: FormStepResult) => {
    if (metricsClient) {
      metricsClient.increment('fmp.questionnaire.back', 1, {
        stepName,
      })
    }

    // Use the store action to handle previous step
    handlePrevStep({ stepName, results })
  }

  // Handle errors
  const errorCta = () => {
    handleErrorCta()

    persistLpAnswers(formResults)

    // Fire error callback from parent site
    if (onErrorCta) {
      onErrorCta(formResults)
    }
  }

  const handleError = (error: unknown) => {
    incense(error)
      .captureDetails({
        name: 'Questionnaire.Error',
        message: 'Find My Plan questionnaire | Generic Odyssey Error',
      })
      .sendError()
  }

  // TODO: Add loading state UI here (spinner/skeleton) when monarchIsLoading === true
  // or when waiting for questionSet to be defined
  if (filteredQuestions.length === 0 || monarchIsLoading) {
    return null
  }

  return (
    <QuestionnaireActionsContext.Provider value={{ onSkipToResults: handleSkipToResults }}>
      <Questionnaire
        formFlow={filteredQuestions}
        handleFinish={handleFinish}
        handleNext={handleNext}
        handleBack={handleBack}
        handleError={handleError}
        errorCta={errorCta}
        onStepLoaded={handleStepLoaded}
      />
    </QuestionnaireActionsContext.Provider>
  )
}

export default QuestionnaireWrapper
